import type { InventarioRow } from '@/types'

export interface AlmacenTotal { almacen: string; total: number }
export interface DonutSlice { almacen: string; total: number; pct: number }

const SIN_ALMACEN = '(sin almacén)'

/** Suma `cantidadFisicaDisponible` por almacén, orden descendente por total. */
export function sumUnitsByAlmacen(rows: InventarioRow[]): AlmacenTotal[] {
  const map = new Map<string, number>()
  for (const r of rows) {
    const key = r.almacen ?? SIN_ALMACEN
    map.set(key, (map.get(key) ?? 0) + r.cantidadFisicaDisponible)
  }
  return [...map.entries()]
    .map(([almacen, total]) => ({ almacen, total }))
    .sort((a, b) => b.total - a.total)
}

/**
 * Top `maxSlices` almacenes por unidades + bucket "Otros" (solo si hay más).
 * pct sobre el gran total, redondeado a 1 decimal; el último ajusta para sumar 100.
 * Devuelve [] si no hay unidades.
 */
export function toDonutSlices(totals: AlmacenTotal[], maxSlices: number, otrosLabel: string): DonutSlice[] {
  const grand = totals.reduce((s, t) => s + t.total, 0)
  if (grand <= 0 || totals.length === 0) return []

  let head: AlmacenTotal[]
  if (totals.length > maxSlices) {
    const otrosTotal = totals.slice(maxSlices).reduce((s, t) => s + t.total, 0)
    head = totals.slice(0, maxSlices)
    if (otrosTotal > 0) head = [...head, { almacen: otrosLabel, total: otrosTotal }]
  } else {
    head = totals
  }

  const rounded = head.map(t => ({ almacen: t.almacen, total: t.total, pct: Math.round((t.total / grand) * 1000) / 10 }))
  const sumRounded = rounded.reduce((s, x) => s + x.pct, 0)
  const last = rounded[rounded.length - 1]
  last.pct = Math.max(0, Math.round((last.pct + (100 - sumRounded)) * 10) / 10)
  return rounded
}
